/**
 * Glassmorphism Toast Utilities
 * Custom toast notifications with glassmorphism styling.
 * 
 * Extracted from App.js for better code organization.
 */

import React from 'react';
import { toast } from 'sonner';
import { showPremiumToast, PremiumToast } from '../components/notifications/PremiumToast';

/**
 * Role display names mapping
 */
const ROLE_NAMES = {
  'admin': 'Admin',
  'order_desk': 'Order Desk',
  'processor': 'Tax Processor',
  'tax_processor': 'Tax Processor',
  'Tax Processor': 'Tax Processor',
  'search_processor': 'Search Processor',
  'Search Processor': 'Search Processor',
  'tax_search_team_leader': 'Tax Search Team Leader',
  'title_search_team_leader': 'Title Search Team Leader',
  'team_leader': 'Team Leader',
  'jurisdiction_manager': 'Jurisdiction Manager',
  'qc_specialist': 'QC Specialist',
  'it_support': 'IT Support',
  'it_manager': 'IT Manager',
  'hr': 'HR',
  'hr_manager': 'HR Manager',
  'accounting': 'Accounting',
};

/**
 * Glassmorphism toast styles.
 *
 * Feb 2026 UX fix (round 4 — contrast & safe-area):
 *  - Bumped background opacity from 0.25 → 0.92 because the
 *    25%-translucent white was invisible against the dark blue
 *    login page background — users reported the toast looked
 *    "half clipped" but it was actually just blending in.
 *  - Stronger shadow + a 1px crisp white border for definition.
 *  - Drop `pointerEvents:'none'` so users can dismiss the toast.
 *  - Add `maxWidth: min(360px, 100vw - 48px)` so the toast never
 *    overflows the viewport on narrow screens.
 *  - Add `boxSizing: 'border-box'` so padding + minWidth play nicely.
 */
const glassmorphismStyle = {
  background: 'rgba(255, 255, 255, 0.92)',
  backdropFilter: 'blur(14px) saturate(140%)',
  WebkitBackdropFilter: 'blur(14px) saturate(140%)',
  border: '1px solid rgba(255, 255, 255, 0.7)',
  borderRadius: '14px',
  padding: '14px 18px',
  boxShadow:
    '0 10px 30px -5px rgba(2, 8, 23, 0.35), ' +
    '0 4px 10px -2px rgba(2, 8, 23, 0.25), ' +
    '0 0 0 1px rgba(255, 255, 255, 0.05) inset',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  minWidth: '280px',
  maxWidth: 'min(360px, calc(100vw - 48px))',
  width: 'auto',
  boxSizing: 'border-box',
  // No custom slide animation here — sonner handles its own enter/exit
  // animation. The previous `animation: 'slideInFromTop 0.4s'` was
  // stacking with sonner's transform and producing apparent vertical
  // clipping on certain Chrome zoom levels.
};

/**
 * Choose a position based on viewport width.
 * Desktop / tablet → top-right (matches the rest of the app).
 * Mobile-narrow (< 640px) → top-center so the toast doesn't crowd
 * the address bar / right edge.
 */
const pickPosition = () => {
  try {
    if (typeof window !== 'undefined' && window.innerWidth < 640) {
      return 'top-center';
    }
  } catch (_) { /* SSR / test env */ }
  return 'top-right';
};

/**
 * Show glassmorphism welcome toast
 * @param {string} userName - User's full name
 * @param {string} userRole - User's role
 */
export const showGlassmorphismWelcome = (userName, userRole) => {
  // PREMIUM-NOTIFY-001 — compact animated success toast, 3 s auto-dismiss
  const firstName = userName?.split(' ')[0] || 'there';
  showPremiumToast({ id: 'login-welcome', variant: 'success', title: `Welcome back, ${firstName}!`,
    body: "You're successfully signed in.", duration: 3000, testId: 'login-success-toast' });
};

/**
 * Show glassmorphism logout toast
 */
export const showGlassmorphismLogout = () => {
  toast.custom(
    (t) => (
      <div style={glassmorphismStyle}>
        <div style={{ fontSize: '24px', flexShrink: 0 }}>👋</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ 
            color: '#1e293b', 
            fontSize: '15px', 
            fontWeight: '600',
            marginBottom: '2px'
          }}>
            You've been logged out
          </div>
          <div style={{ 
            color: '#475569', 
            fontSize: '13px',
            fontWeight: '400'
          }}>
            See you again soon!
          </div>
        </div>
      </div>
    ),
    {
      duration: 2500, // Slightly longer than welcome since it's a goodbye
      position: pickPosition(),
    }
  );
};

/**
 * Unified Logout Notice toast — single rendering surface for ALL
 * 5 logout scenarios. Reads `{ scenario, copy }` produced by
 * `readLogoutNotice()` (utils/logoutNotice.js).
 *
 * Tone-aware border colour:
 *   info    → soft blue accent  (manual / idle_timeout / token_expired)
 *   warning → soft amber accent (role / password / token_revoked / invalid)
 *
 * Position uses `pickPosition()` (top-right on desktop, top-center
 * on mobile-narrow).
 */
export const showGlassmorphismLogoutNotice = (notice) => {
  if (!notice || !notice.copy) return;
  const { title, body, tone } = notice.copy;
  // PREMIUM-NOTIFY-001 — manual logout completes the "Signing you out…" toast (same id) with a green check.
  if (notice.scenario === 'manual') {
    showPremiumToast({ id: 'logout-flow', variant: 'success', title: 'Successfully signed out',
      body: 'See you next time!', duration: 3000, testId: 'logout-success-toast' });
    return;
  }
  toast.custom((t) => (
    <PremiumToast variant={tone === 'warning' ? 'warning' : 'info'} title={title} body={body}
      testId={`logout-notice-${notice.scenario}`} onClose={() => toast.dismiss(t)} />
  ), { id: 'logout-flow', duration: 4000, position: pickPosition(), unstyled: true, className: 'ptoast-host',
       style: { padding: 0, background: 'transparent', border: 0, boxShadow: 'none', width: 'fit-content', maxWidth: 'calc(100vw - 32px)' } });
};

/** PREMIUM-NOTIFY-001 — "Signing you out…" progress toast shown the moment logout starts. */
export const showLogoutInProgress = () => {
  showPremiumToast({ id: 'logout-flow', variant: 'loading', title: 'Signing you out…',
    body: 'Please wait a moment.', duration: 15000, testId: 'logout-progress-toast' });
};

/**
 * Show glassmorphism success toast
 * @param {string} title - Toast title
 * @param {string} description - Toast description
 */
export const showGlassmorphismSuccess = (title, description) => {
  toast.custom(
    (t) => (
      <div style={glassmorphismStyle}>
        <div style={{ fontSize: '24px', flexShrink: 0 }}>✅</div>
        <div style={{ flex: 1 }}>
          <div style={{ 
            color: '#1e293b', 
            fontSize: '15px', 
            fontWeight: '600',
            marginBottom: '2px'
          }}>
            {title}
          </div>
          {description && (
            <div style={{ 
              color: '#475569', 
              fontSize: '13px',
              fontWeight: '400'
            }}>
              {description}
            </div>
          )}
        </div>
      </div>
    ),
    {
      duration: 3000, // Reduced from 4000ms - success messages auto-dismiss faster
      position: pickPosition(),
    }
  );
};

export { ROLE_NAMES };
